import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Dropdown } from "semantic-ui-react";
import Anchor from "../Anchor"; // Reuse your existing Anchor component
import Empty from "../Empty";
import html2canvas from "html2canvas";
import {
  calculateHeatmap,
  vertical_sorted_names,
  horizontal_sorted_names,
  factorTitleSorted,
} from "app/audit/simple/Heatmap/calculations"; // The same utility file you had
import FactorRow from "./Heatmap/FactorRow"; // Keep FactorRow for Culture breakdown
import { SET_DATA_ACNHOR } from "constants/actions";
import SlideBackground from "assets/images/UI/slide_background.svg";

const VIEW_OPTIONS = [
  { key: "culture", text: "Culture", value: "culture" },
  { key: "outcome", text: "Outcome", value: "outcome" },
];

const HeatmapSlide = ({ slideData, updateSlideData, loading }) => {
  const dispatch = useDispatch();

  // Two refs:
  const visibleHeatmapRef = useRef(null); // Shown on screen (scaled)
  const exportHeatmapRef = useRef(null);  // Hidden, unscaled for html2canvas
  const containerRef = useRef();

  const [scale, setScale] = useState(1);
  const skipAnchorEffect = useRef(false);

  // Redux Store
  const anchor = useSelector((state) => state.audit?.anchor, shallowEqual);
  const core_data = useSelector((state) => state.audit?.core_data, shallowEqual);
  const filtered_data = useSelector(
    (state) => state.audit?.filtered_data,
    shallowEqual
  );

  // Local State
  const [viewMode, setViewMode] = useState("culture"); // default to Culture
  const [tableData, setTableData] = useState([]);
  const [verticalTitles, setVerticalTitles] = useState([]);
  const [horizontalTitles, setHorizontalTitles] = useState([]);
  const [factorTitles, setFactorTitles] = useState([]);
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(10);
  const [selected, setSelected] = useState(0);
  const [isDomLoaded, setIsDomLoaded] = useState(false);

  // Update the scale factor based on container and content width.
  const updateScale = () => {
    const container = containerRef.current;
    const content = visibleHeatmapRef.current;
    if (container && content) {
      const containerWidth = container.clientWidth;
      const contentWidth = content.scrollWidth;
      // Compute scale; never scale above 100% (scale=1)
      const newScale = Math.min(1, containerWidth / contentWidth);
      setScale(newScale);
    }
  };

  useEffect(() => {
    setIsDomLoaded(true);
    updateScale(); // initial calculation
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  useEffect(() => {
    if (tableData.length > 0 && !loading) {
      updateScale();
    }
  }, [tableData, isDomLoaded, loading]);

  // Restore saved slide data if present
  useEffect(() => {
    if (slideData && slideData.viewMode) {
      setViewMode(slideData.viewMode);
    }
    if (slideData && slideData.anchor && slideData.anchor !== anchor) {
      skipAnchorEffect.current = true;
      dispatch({ type: SET_DATA_ACNHOR, payload: slideData.anchor });
    }
  }, [slideData]);

  // Capture the hidden, unscaled heatmap
  useEffect(() => {
    if (exportHeatmapRef.current && !loading) {

      html2canvas(exportHeatmapRef.current, { scale: 2,backgroundColor: null })
      .then((canvas) => {
        const imgURI = canvas.toDataURL("image/png");
        updateSlideData({
          ...slideData,
          heatmapImage: imgURI,
          anchor,
          slide_template_id: 5,
          title:`Heatmap of ${viewMode === "culture" ? "Culture" : "Outcomes"}`,
          content:`This slide shows a heatmap of each ${viewMode} dimension across your selected anchor/category.`              // etc...
        });
      });
    
    }
  }, [exportHeatmapRef, anchor, loading,scale]);

  // If anchor changes from user input in Anchor component
  // useEffect(() => {
  //   if (skipAnchorEffect.current) {
  //     skipAnchorEffect.current = false; // Reset the flag
  //     return; // Skip updating slideData
  //   }
  //   if (slideData?.anchor !== anchor) {
  //     updateSlideData({ ...slideData, anchor });
  //   }
  // }, [anchor, scale]);

  // Recalculate Heatmap data whenever filter or anchor changes
  useEffect(() => {
    if (!core_data || !filtered_data || !anchor) return;

    // Convert filtered_data to an array of arrays if needed
    let dataFormat;
    if (Array.isArray(filtered_data[0])) dataFormat = filtered_data;
    else dataFormat = [filtered_data];

    let rule = core_data?.Whitelabel?.rule
      ? parseInt(core_data?.Whitelabel?.rule)
      : 2;

    let data = calculateHeatmap(
      core_data,
      dataFormat,
      anchor,
      core_data.outcomeQuestions ?? null,
      null
    ).filter((f) => f.responses > rule);

    setTableData(data);
    setVerticalTitles(vertical_sorted_names(data));
    setHorizontalTitles(horizontal_sorted_names(data));
    setFactorTitles(factorTitleSorted(data));

    // Compute min and max
    let min_score = 100;
    let max_score = 0;

    data.forEach((item) => {
      let cultureScore = item.culture_scores?.overall_scores;
      if (cultureScore < min_score) min_score = cultureScore;
      if (cultureScore > max_score) max_score = cultureScore;

      // outcome scores if you want them
      Object.values(item.outcome_scores?.overall_scores ?? {}).forEach((val) => {
        if (val < min_score) min_score = val;
        if (val > max_score) max_score = val;
      });
    });

    setMin(min_score * 10);
    setMax(max_score * 10);
  }, [core_data, filtered_data, anchor]);

  // Whenever tableData, viewMode, etc. changes => Update Slide Data
  // useEffect(() => {
  //   updateSlideData({
  //     ...slideData,
  //     anchor,
  //     scale,
  //     viewMode,
  //     slide_template_id: 5,
  //   });
  // }, [viewMode, tableData]);

  const selectRow = (index) => {
    setSelected(index === selected ? null : index);
  };

  const getTotalResponses = () =>
    tableData.map((item) => item.responses).reduce((a, b) => a + b, 0);

  const findByOptionName = (name) => {
    return tableData.find((item) => item.option === name);
  };

  const getScore = (title, item) => {
    const row = findByOptionName(item);
    if (!row) return "";

    if (viewMode === "culture") {
      let val = row?.culture_scores?.overall_scores;
      return val ? (val * 10).toFixed(0) : "";
    } else {
      let val = row?.outcome_scores?.overall_scores[title.name];
      return val ? (val * 10).toFixed(0) : "";
    }
  };

  const getColorGrade = (score) => {
    if (!score) return "#F8FAFF;";
    let normalizedScore = (score - min) / (max - min);
    if (score > max) normalizedScore = 1;
    if (score < min) normalizedScore = 0;

    const redStart = { r: 241, g: 80, b: 98 }; // #F15062
    const greenEnd = { r: 39, g: 205, b: 167 }; // #27CDA7

    let red, green, blue;
    if (normalizedScore <= 0.5) {
      const ratio = normalizedScore * 2;
      red = redStart.r + Math.floor((255 - redStart.r) * ratio);
      green = redStart.g + Math.floor((255 - redStart.g) * ratio);
      blue = redStart.b + Math.floor((255 - redStart.b) * ratio);
    } else {
      const ratio = (normalizedScore - 0.5) * 2;
      red = 255 + Math.floor((greenEnd.r - 255) * ratio);
      green = 255 + Math.floor((greenEnd.g - 255) * ratio);
      blue = 255 + Math.floor((greenEnd.b - 255) * ratio);
    }
    return `rgb(${red}, ${green}, ${blue})`;
  };

  // Render the same table content in both scaled & hidden versions
  const renderHeatmapTable = () => {
    if (getTotalResponses() === 0) {
      return <Empty />;
    }
    return (
      <Table>
        <thead>
          <tr>
            <TableHeader />
            <TableHeader>
              <SlantedHeader>Totals</SlantedHeader>
            </TableHeader>
            {verticalTitles.map((title) => (
              <TableHeader key={title}>
                <SlantedHeader>{title}</SlantedHeader>
              </TableHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          <TableRow3>
            <FirstColumnCellResponse>responses</FirstColumnCellResponse>
            <TableCell2>{getTotalResponses()}</TableCell2>
            {verticalTitles.map((title) => (
              <TableCell2 key={title}>{findByOptionName(title)?.responses}</TableCell2>
            ))}
          </TableRow3>

          {horizontalTitles.map((title, idx) => {
            const overallVal = (title.score * 10).toFixed(0);
            if (
              (viewMode === "culture" && title.name !== "Culture") ||
              (viewMode !== "culture" && title.name === "Culture")
            ) {
              return null;
            }

            return (
              <React.Fragment key={title.name + idx}>
                <TableRow onClick={() => selectRow(idx)}>
                  <FirstColumnCell>{title.name}</FirstColumnCell>
                  <TableCell>{overallVal}</TableCell>
                  {verticalTitles.map((vtitle) => {
                    const cellVal = getScore(title, vtitle);
                    return (
                      <TableCell
                        key={vtitle}
                        color={getColorGrade(cellVal)}
                      >
                        {cellVal}
                      </TableCell>
                    );
                  })}
                </TableRow>

                {/* Factor rows if user selected "culture" */}
                {viewMode === "culture" &&
                  factorTitles.map((factor) => (
                    <FactorRow
                      key={factor.name}
                      summary={factor}
                      data={tableData}
                      index={0}
                      selected={0}
                      min={min}
                      max={max}
                    />
                  ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    );
  };

  if (!core_data) {
    return <Empty />;
  }
  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Container ref={containerRef}>
      <T1>Heatmap Slide</T1>
      <SubTitle>Slide Options</SubTitle>
      <OptionArea>
        <div style={{ marginRight: 20 }}>
          <OptionTitle>View Mode</OptionTitle>
          <Dropdown
            placeholder="View Culture or Outcome"
            selection
            options={VIEW_OPTIONS}
            value={viewMode}
            onChange={(e, { value }) => setViewMode(value)}
          />
        </div>
        <div>
          <OptionTitle>Category (Anchor)</OptionTitle>
          <Anchor />
        </div>
      </OptionArea>

      <SlidePreview>
        <SlideTitle>
          Heatmap of {viewMode === "culture" ? "Culture" : "Outcomes"}
        </SlideTitle>
        <Description>
          This slide shows a heatmap of each {viewMode} dimension across your
          selected anchor/category.
        </Description>

        {/* 1) Visible scaled version */}
        <ScaleWrapper scale={scale}>
          <Content ref={visibleHeatmapRef}>
            {renderHeatmapTable()}
          </Content>
        </ScaleWrapper>

        {/* 2) Hidden unscaled version for html2canvas */}
        <HiddenExportWrapper ref={exportHeatmapRef}>
          {renderHeatmapTable()}
        </HiddenExportWrapper>

        <SlideFooter>innerlogic</SlideFooter>
      </SlidePreview>
    </Container>
  );
};

export default HeatmapSlide;

/* --- Styled Components Below --- */

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  font-family: "Raleway";
  position: relative;
`;

const T1 = styled.h2`
font-size: 20px;
font-weight: 600;
margin-bottom:10px;
color:#2A3039;
`;

const SubTitle = styled.div`
font-size: 14px;
margin-top:20px;
margin-bottom:5px;
font-weight:600;
`;

const OptionArea = styled.div`
margin-bottom:20px;
    display:flex;
    padding:20px;
    background-color:#f9fbfd;
    width:100%;
`;

const OptionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 5px;
`;

const SlidePreview = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  margin-top: 20px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    to right,
    #ecf5f9,
    #e9f4f8,
    #e5f2f6,
    #e1f1f4,
    #ddeff2,
    #d9eef0,
    #d5edef,
    #d1ebed,
    #ceeaeb,
    #cae8e9
  );
`;

const SlideTitle = styled.h3`
  margin: 0;
  margin-bottom: 10px;
  color: #2d70e2;
`;

const Description = styled.div`
  font-size: 14px;
  margin-bottom: 20px;
  color: #2a3039;
`;

const Content = styled.div`
  position: relative;
  min-height: 250px;
  margin-top: 100px;
`;

const Table = styled.table`
  border-collapse: collapse;
  margin-top: 30px;
`;

const TableHeader = styled.th`
  padding: 0;
  height: 40px;
  width: 40px;
  min-width: 40px;
  vertical-align: bottom;
  position: relative;
`;

const SlantedHeader = styled.div`
  transform: rotate(-45deg);
  transform-origin: left bottom;
  white-space: nowrap;
  position: absolute;
  bottom: -16px;
  left: 19px;
  width: 175px;
  line-height: 1;
  border-top: 1px solid #dfdfdf;
  display: flex;
  padding-left: 35px;
  padding-top: 10px;
  color: #2d70e2;
  font-weight: 500;
  text-overflow: ellipsis;
  background-color: transparent;
  font-size: 10px;
`;

const TableRow = styled.tr`
  height: 35px;
  cursor: pointer;
`;

const TableRow3 = styled.tr`
  height: 35px;
  background-color: #f8faff;
`;

const TableCell = styled.td`
  border: 1px solid #ddd;
  padding: 0;
  text-align: center;
  width: 50px;
  min-width: 50px;
  background-color: ${(props) => props.color || "#F8FAFF"};
`;

const TableCell2 = styled.td`
  border: 1px solid #ddd;
  padding: 0;
  text-align: center;
  width: 50px;
  min-width: 50px;
  font-size: 12px;
  font-weight: 600;
  background-color: #f8faff;
`;

const FirstColumnCell = styled.td`
  border: 1px solid #ddd;
  padding: 0 8px;
  text-align: left;
  width: 350px;
  background-color: #f8faff;
  font-size: 14px;
  font-weight: 500;
  color: #2d70e2;
`;

const FirstColumnCellResponse = styled.td`
  border: 1px solid #ddd;
  padding: 0 8px;
  text-align: left;
  width: 350px;
  background-color: #f8faff;
  text-transform: uppercase;
  font-size: 10px;
  font-weight: 500;
`;

const SlideFooter = styled.div`
  position: absolute;
  bottom: 10px;
  right: 20px;
  color: #888;
  font-size: 10px;
`;

const SlideBackgroundImage = styled.img`
  position: absolute;
  bottom: -50%;
  right: 0;
  opacity: 0.3;
`;

const ScaleWrapper = styled.div`
  transform: scale(${(props) => props.scale});
  transform-origin: top left;
`;

const HiddenExportWrapper = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
  pointer-events: none;
`;